<template>
  <div style="width: 100%;">
         <v-row>
      <v-col>
    <v-container fluid>
      <v-row>
        <v-col class="d-flex flex-row align-center">
          <v-btn @click="$router.go(-1)" text><v-icon>mdi-arrow-left</v-icon></v-btn>
          <h1>Stock History for {{productLabel}}</h1>
          <v-progress-circular color="success" indeterminate v-if="loader"/>
        </v-col>
      </v-row>
      <v-row v-if="data && groupedData && processedData">
        <v-col>
          <div>
            <span class="d-flex flex-row flex-wrap align-center mb-2" style="gap: 4px;">
              <h3>Table Filters</h3>
              <v-btn x-small color="info" fab @click="resetFilters"><v-icon>mdi-restart</v-icon></v-btn>
              <v-btn x-small color="error" fab @click="clearAllFilters"><v-icon>mdi-close-box</v-icon></v-btn>
            </span>
            <div class="mb-4">
              <span class="d-flex flex-row">
                <v-menu
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="tableOptions.startDate"
                        label="Start Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        outlined
                        v-bind="attrs"
                        hide-details
                        v-on="on"
                    />
                  </template>
                  <v-date-picker
                      v-model="tableOptions.startDate"
                  />
                </v-menu>
                <v-btn @click="tableOptions.endDate=tableOptions.startDate" color="warning" class="mx-2 mt-2"><v-icon>mdi-arrow-right</v-icon></v-btn>
                <v-menu
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="tableOptions.endDate"
                      label="End Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      v-bind="attrs"
                      hide-details
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                      v-model="tableOptions.endDate"
                  />
                </v-menu>
                <v-spacer/>
              </span>

              <span class="d-flex flex-row mt-1" style="gap: 4px;">
                <v-btn small color="error" @click="clearDates">Clear Dates</v-btn>
                <v-btn small color="info" @click="dailyRecords">Today</v-btn>
                <v-btn small color="info" @click="monthlyRecords">This Month</v-btn>
                <v-btn small color="info" @click="yearlyRecords">This Year</v-btn>
              </span>
            </div>
            <div>
              <span class="d-flex flex-row align-center" style="gap: 4px;">
                <v-autocomplete
                    :disabled="tableOptions.excludeUser.length>0"
                    v-model="tableOptions.includeUser"
                    :items="getUserCache"
                    :item-text="item=>(item.firstName||'')+' '+(item.lastName||'')+(item.deletedAt?' (DELETED USER)':'')"
                    item-value="id"
                    hide-details
                    outlined
                    multiple
                    label="Include Users"
                    clearable
                ></v-autocomplete>
                <v-btn color="warning" @click="swapUserFields"><v-icon>mdi-swap-horizontal</v-icon></v-btn>
                <v-autocomplete
                    :disabled="tableOptions.includeUser.length>0"
                    v-model="tableOptions.excludeUser"
                    :items="getUserCache"
                    multiple
                    :item-text="item=>(item.firstName||'')+' '+(item.lastName||'')+(item.deletedAt?' (DELETED USER)':'')"
                    item-value="id"
                    hide-details
                    outlined
                    label="Exclude Users"
                    clearable
                ></v-autocomplete>
              </span>
            </div>
            <div class="d-flex flex-row mb-2" style="gap: 16px;">
              <v-switch hide-details label="Show Location Breakdown" v-model="tableOptions.showLocationBreakdown" color="info"/>
              <span class="d-flex flex-column">
                <v-switch hide-details label="Show Available QTYs" v-model="tableOptions.showAvailableQtyBreakdown" color="info"/>
                <v-switch v-if="tableOptions.showAvailableQtyBreakdown" hide-details label="Only Δ Rows" v-model="tableOptions.showAvailableChangesOnly" color="info"/>
              </span>
              <span class="d-flex flex-column">
                <v-switch hide-details label="Show In Stock QTYs" v-model="tableOptions.showInStockQtyBreakdown" color="info"/>
                <v-switch v-if="tableOptions.showInStockQtyBreakdown" hide-details label="Only Δ Rows" v-model="tableOptions.showInStockChangesOnly" color="info"/>
              </span>
            </div>
            <div class="d-flex flex-row flex-wrap align-center mb-2">
              <v-autocomplete :disabled="tableOptions.allActions" hide-details dense clearable outlined :items="urlDictionary" label="Actions" item-text="label" item-value="label" chips deletable-chips v-model="tableOptions.visibleActions" multiple/>
              <v-switch label="All Actions" v-model="tableOptions.allActions" @change="checkAllActions" color="info" hide-details/>
            </div>
          </div>
          <div>
            <h3>Exports</h3>
            <span class="d-flex flex-row flex-wrap mt-2 align-center" style="gap: 8px;">
              <span>
                <v-select style="width: 150px;" outlined dense hide-details :items="exportOptions.types" label="Export As" v-model="exportOptions.type"/>
              </span>
              <span v-if="exportOptions.type==='csv'" class="d-flex flex-row">
                <json-excel :stringifyLongNum="true" :name="exportOptions.name||`VEC Report.${exportOptions.type}`" :data="exportOptions.items" :fields="exportOptions.csvHeaders" :type="exportOptions.type">
                  <v-btn :color="exportOptions.color||'info'">{{exportOptions.text||'Export as CSV'}}</v-btn>
                </json-excel>
              </span>
              <span v-if="exportOptions.type==='xlsx' || exportOptions.type==='xls'">
                <vue-excel-xlsx
                    :data="exportOptions.items"
                    :columns="exportOptions.excelHeaders"
                    :file-name="exportOptions.name||`VEC Report`"
                    :file-type="exportOptions.type"
                    :sheet-name="exportOptions.sheet||'Data'"
                >
                  <v-btn :color="exportOptions.color||'info'">{{exportOptions.text||'Export for Excel'}}</v-btn>
                </vue-excel-xlsx>
              </span>
              <b>{{ (visibleItems===groupedData.length)?'All':`${visibleItems}/${groupedData.length}` }} Visible</b>
            </span>
            <span class="d-flex flex-row align-center mb-2" style="gap: 4px;">
              <span>Only what is visible on the table will be exported. If you want all data, click here to clear filters and show all.</span>
              <v-btn x-small color="info" @click="showAll">Show All Data</v-btn>
            </span>

          </div>
          <span>
            <v-data-table mobile-breakpoint="0"
                :items = processedData
                :headers = simpleViewTable
                :items-per-page.sync="tableOptions.pageLimit"
                sort-by="id"
                :sort-desc="true"
                @current-items="itemsChanged"
                :footer-props="{
                  itemsPerPageOptions: [10, 25, 50, 100, -1]
                }"
                dense
            >
              <template v-slot:item.createdAt="{ item }">
                <span>{{ utils.formatDate(item.createdAt, "withTime") }}</span>
              </template>
              <template v-slot:item.totalAvailChange="{ item }">
                <span :class="item.totalAvailChange>=0?'success--text':'error--text'">{{ item.totalAvailChange }}</span>
              </template>
              <template v-slot:item.totalChange="{ item }">
                <span :class="item.totalChange>=0?'success--text':'error--text'">{{ item.totalChange }}</span>
              </template>
              <template v-slot:item.changed="{ item }">
                <span>
                  <div v-for="(line, i) of item.changed" :key="i">
                    <span v-html="line"/>
                  </div>
                </span>
              </template>
            </v-data-table>
          </span>
        </v-col>
      </v-row>
    </v-container>
      </v-col>
     </v-row>

    <v-snackbar v-model="snackObj.state" :timeout="3000" :color="snackObj.color">
      {{ snackObj.text }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snackObj.state = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<style scoped>
  table{
    border-collapse: collapse;
  }
  tr{
    border-bottom: 1px solid #ccc;
  }
  .CodeMirrorHeightAuto *{
    height: auto;
  }
</style>
<script>
  import axios from 'axios';
  import {mapGetters} from "vuex";
  import utils from "../../plugins/helpers"
  import moment from "moment/moment";
  export default {
    data () {
      return {
        utils: utils,
        deleteDialog: false,
        deleteConfirmed: true,
        loader: false,
        snackObj: {
          state: false,
          color: '',
          text: ''
        },

        filters: {
          startDate: null,
          endDate: null,
          userIds: [],
          excludedUserIds: []
        },

        show: {
          metadata: false,
          connections: false,
          data: false,
          changed: true,
          simpleView: true
        },

        actions: [{text: "Create", value: "create"}, {text: "Update", value: "update"}, {text: "Delete", value: "destroy"}, {text: "Restore", value: "restore"}],

        data: null,
        groupedData: null,
        processedData: [],

        pageCount: 1,
        pageLength: 20,
        startLength: 4,
        endLength: 3,

        requestedLog: {
          loading: false,
          data: null
        },

        allBranches: [],

        tableOptions: {},

        tableOptionsBase: {
          showLocationBreakdown: false,
          showAvailableQtyBreakdown: false,
          showAvailableChangesOnly: false,
          showInStockQtyBreakdown: true,
          showInStockChangesOnly: false,
          visibleActions: [],
          allActions: true,
          includeUser: [],
          excludeUser: [],
          pageLimit: 50,
          startDate: null,
          endDate: null,
          sortBy: 'id',
          sortByAsc: true
        },

        exportOptions: {
          type: 'xlsx',
          types: [],
          items: [],
          name: "No data",
          text: "No data",
          color: "secondary"
        },

        totalCount: 0,

        productLabel: "",

        simpleViewTableBase: [
          {
            text: 'Log #',
            align: 'start',
            sortable: true,
            value: 'id',
            width: '100'
          },
          { text: 'Action', value: 'parsedAction', sortable: true },
          { text: 'Document ID', value: 'pertains', sortable: true },
          { text: 'User', value: 'userId', width:'150', sortable: true },
          { text: 'What changed?', value: 'changed', sortable: false },
          { text: 'Occurred At', value: 'createdAt', width: '150', sortable: true },
        ],

        urlDictionary: []
      }
    },
    created(){
      this.resetFilters();
    },
    async mounted(){
      this.allBranches = this.getBranches.map(x => {
        return {
          text: x.name,
          value: x.id
        }
      }).sort((a, b) => a.text.localeCompare(b.text));

      this.urlDictionary = this.urlDictionary.sort((a, b) => a.label.localeCompare(b.label))

      await this.getAllData();
    },
    computed: {
      ...mapGetters(['getEndpoint', "isAllowed", 'lookupUsername', 'getUserCache', 'getBranches', 'getGlobalValue', 'paymentTypes', 'lookupLocation', 'lookupBranch']),
      getTotalPages(){
        return parseInt((this.data.length/this.pageLength)+(this.data.length%this.pageLength===0?0:1))
      },
      visibleItems(){
        if(this.tableOptions.pageLimit===-1) return this.processedData.length;

        return Math.min(this.tableOptions.pageLimit, this.processedData.length)
      },
      simpleViewTable(){
        let headers = [];

        // 1️⃣ Locations first
        if (this.tableOptions.showLocationBreakdown) {
          this.allBranches.forEach(loc => {
            if (this.tableOptions.showAvailableQtyBreakdown) {
              headers.push({
                text: `${loc.text} Avail`,
                value: `availObj.branch_${loc.value}`,
                sortable: false
              });
            }
            if (this.tableOptions.showInStockQtyBreakdown) {
              headers.push({
                text: `${loc.text} QTY`,
                value: `qtyObj.branch_${loc.value}`,
                sortable: false
              });
            }
          });
        }

        // 2️⃣ Total columns
        if (this.tableOptions.showAvailableQtyBreakdown) {
          headers.push(
              { text: 'Total Avail', value: 'totalAvail', sortable: false },
              { text: 'Δ Avail', value: 'totalAvailChange', sortable: false }
          );
        }

        if (this.tableOptions.showInStockQtyBreakdown) {
          headers.push(
              { text: 'Total InStock', value: 'totalQty', sortable: false },
              { text: 'Δ InStock', value: 'totalChange', sortable: false }
          );
        }

        // 3️⃣ Then static headers (ID, Action, etc.)
        let final = [...this.simpleViewTableBase];

        let insertPoint = 5;
        final.splice(insertPoint, 0, ...headers)
        return final;
      }
    },
    watch: {
      tableOptions: {
        async handler () {
          this.filterData();
        },
        deep: true,
      }
    },
    methods: {
      getDictUrl(url, action=null){
        let d = this.urlDictionary.filter(x => url===x.url || url.startsWith(x.url) || url.includes(x.url))
        if(d.length>=1){
          d = d.find(x => url===x.url) || d[0];
        }

        if(d?.action && d?.action!==action) return null
        return d
      },
      snack(text, color=""){
        this.snackObj.text = text;
        this.snackObj.state = true;
        this.snackObj.color = color;
      },
      getAction(action){
        let a = this.actions.find(x => x.value===action);
        return a?.text || ""
      },
      async getAllData(){
        try {
          this.loader = true;

          let res = await axios.get(`${this.getEndpoint}/api/products/${this.$route.params.id}`)
          if(res.data.error) throw res.data.error
          if(res.data.data){
            this.productLabel = [`ID: ${res.data.data.id}`, res.data.data.name, res.data.data.sku].filter(x => x).join(" | ")
          }

          this.$forceUpdate();

          await this.fetchRecords()
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
        } finally {
          this.loader = false;
        }
      },
      async fetchRecords(){
        try {
          this.loader = true;

          this.data = null;
          this.totalCount = 0;

          let res = await axios.post(`${this.getEndpoint}/api/logs/productHistory/${this.$route.params.id}`, this.filters)
          if(res.data.error) throw res.data.error

          this.data = res.data.data;
          this.groupedData = res.data.formatted;
          let tempDict = res.data.urlDictionary.filter(x => x.distinctions);
          let additional = [];
          for(let dict of tempDict){
            for(let dis of dict.distinctions){
              additional.push({label: dis[1]});
            }
          }
          this.urlDictionary = [...res.data.urlDictionary, ...additional];

          this.filterData();

        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
        } finally {
          this.loader = false;
        }
      },
      filterData(){
        if(!this.groupedData) return;

        let arr = [...this.groupedData];

        let { startDate, endDate } = this.tableOptions;
        let adjustedEnd = endDate ? new Date(new Date(endDate).getTime() + 24 * 60 * 60 * 1000) : null;
        startDate = startDate ? new Date(startDate) : null;

        arr = arr.filter(x => {
          let ca = new Date(x.createdAt);
          if (startDate && adjustedEnd) return ca >= startDate && ca <= adjustedEnd;
          if (startDate) return ca >= startDate;
          if (adjustedEnd) return ca < adjustedEnd;
          return true;
        });

        if(this.tableOptions.showAvailableChangesOnly){
          arr = arr.filter(x => x.totalAvailChange!==0);
        }

        if(this.tableOptions.showInStockChangesOnly){
          arr = arr.filter(x => x.totalChange!==0);
        }

        if(!this.tableOptions.allActions){
          arr = arr.filter(x => this.tableOptions.visibleActions.includes(this.getDictUrl(x.route, x.action)?.label));
        }

        if(this.tableOptions.includeUser.length>0)
          arr = arr.filter(x => this.tableOptions.includeUser.includes(x.userId));
        else if(this.tableOptions.excludeUser.length>0){
          arr = arr.filter(x => !this.tableOptions.excludeUser.includes(x.userId));
        }

        let exportObj = {
          name: `Stock History PL${this.$route.params.id} - ${new Date().toDateString()}`,
          csvHeaders: {},
          excelHeaders: [],
          items: [],
          types: ["xlsx", "xls", "csv"],
          text: "Export Visible Data",
          color: "warning"
        }

        for(let header of this.simpleViewTable){
          exportObj.csvHeaders[header.text] = header.value;
          exportObj.excelHeaders.push({label: header.text, field: header.value});
        }

        this.exportOptions = {
          ...this.exportOptions,
          ...exportObj
        }

        this.processedData = arr;
      },
      itemsChanged(items){
        let visibleItems = [];

        for(let item of items){
          visibleItems.push({
            ...item,
            changed: item.changedMini
          })
        }

        this.exportOptions.items = visibleItems
      },
      checkAllActions(){
        if(!this.tableOptions.allActions){
          this.tableOptions.visibleActions = [];
        }
      },
      clearAllFilters(){
        this.resetFilters();
        this.tableOptions = {
          ...this.tableOptions,
          showInStockQtyBreakdown: false,
        }
      },
      resetFilters(){
        this.tableOptions = {...this.tableOptionsBase};
      },
      showAll(){
        this.resetFilters();
        this.tableOptions = {
          ...this.tableOptions,
          showLocationBreakdown: true,
          showAvailableQtyBreakdown: true,
          showInStockQtyBreakdown: true,
          pageLimit: -1,
        }
      },
      async swapUserFields(){
        if(this.tableOptions.includeUser.length>0){
          let temp = this.tableOptions.includeUser;
          this.tableOptions.includeUser = [];
          this.tableOptions.excludeUser = temp;
        }
        else if(this.tableOptions.excludeUser.length>0){
          let temp = this.tableOptions.excludeUser;
          this.tableOptions.excludeUser = [];
          this.tableOptions.includeUser = temp;
        }
      },
      clearDates(){
        this.tableOptions.startDate = null;
        this.tableOptions.endDate = null;
      },
      async dailyRecords(){
        this.tableOptions.startDate = moment().format("YYYY-MM-DD");
        this.tableOptions.endDate = moment().format("YYYY-MM-DD");
      },
      async monthlyRecords(){
        this.tableOptions.startDate = moment().startOf('month').format("YYYY-MM-DD");
        this.tableOptions.endDate = moment().endOf('month').format("YYYY-MM-DD");
      },
      async yearlyRecords(){
        this.tableOptions.startDate = moment().startOf('year').format("YYYY-MM-DD");
        this.tableOptions.endDate = moment().endOf('year').format("YYYY-MM-DD");
      },
    }
  }
</script>
